import '@/styles/index.css'
import { EVENTS, forceEventOverride, getActiveEvents } from '@/data/events'
import { installErrorReporter, reportManualError } from '@/services/errorReporter'
import { isNativeApp, startNativeShell } from '@/native/nativeShell'
import { runBoot } from './boot'

/**
 *  ENTRY POINT. DELIBERATELY DOES NOT IMPORT THE STORE.
 *
 *  `@/store/store` builds the Redux store from localStorage the moment it is
 *  imported, and that store is the one the page keeps. So the authoritative
 *  save has to be in localStorage BEFORE that import happens — which is why
 *  the mount lives behind an `await import('./mountApp')` at the bottom of this
 *  file, and why nothing above it may reach the store.
 *
 *  Adding `import { store } from '@/store/store'` here would restore the bug
 *  that cost two rounds of fixes: a phone showing level 95 while the tablet was
 *  97, because the only way to apply a late-arriving cloud save was to reload
 *  the page, and the reload was budgeted at one per tab session.
 *  `.verify/check_boot_order.ts` fails if this file's import graph reaches the
 *  store.
 */

// Install the global client-error reporter BEFORE anything else so a crash
// during boot is captured too. Reports stream into the admin god-view
// dashboard's "Recent client errors" panel.
installErrorReporter()

// Dev / admin console helpers — attach to window so you can flip event
// state without editing code. Survives a refresh via localStorage.
//
// Examples:
//   __forceEvent('goldrush-2026-07', true)   // turn it on right now
//   __forceEvent('goldrush-2026-07', false)  // turn it off explicitly
//   __forceEvent(null)                       // clear all overrides
//   __listEvents()                           // catalog all events + dates
//   __activeEvents()                         // who's live right now
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any
  w.__forceEvent   = forceEventOverride
  w.__listEvents   = () => EVENTS.map(e => ({ id: e.id, name: e.name, startAt: e.startAt, endAt: e.endAt }))
  w.__activeEvents = () => getActiveEvents().map(e => ({ id: e.id, name: e.name }))
  // Manual entry to surface a notable error on the admin dashboard
  // without crashing — usable from a try/catch or DevTools.
  w.__reportError  = (msg: string) => reportManualError(msg)
}

// Inside the iOS/Android app the whole game is bundled, so the cache-first
// service worker adds nothing — and it would keep serving the OLD game after an
// app update until its cache expired.
const insideNativeApp = isNativeApp()
void startNativeShell()

// Register the asset-caching service worker so sprites/backgrounds load
// instantly (and offline) after the first visit.
//
// PRODUCTION ONLY. In dev the SW's cache-first strategy would serve stale
// sprites on any secure-context browser (i.e. localhost), so freshly
// regenerated art wouldn't show up until the cache was cleared. In dev we
// instead actively unregister any existing worker and wipe its cache, which
// self-heals a machine that picked one up earlier.
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  if (import.meta.env.PROD && !insideNativeApp) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch(() => {
        /* registration blocked (insecure context) — safe to ignore */
      })
    })
  } else {
    navigator.serviceWorker.getRegistrations().then(
      regs => regs.forEach(r => r.unregister()),
      () => {}
    )
    if (typeof caches !== 'undefined') {
      caches.keys().then(keys => keys.forEach(k => caches.delete(k)), () => {})
    }
  }
}

/**
 *  Resolve the save, THEN build the game.
 *
 *  `runBoot` never throws and never hangs — it has its own network budget and
 *  falls back to the local save with pushing blocked. But a thrown error from
 *  anywhere else in here must still end with a mounted game rather than a
 *  loading screen forever, so the mount is in a `finally`-shaped path: it runs
 *  whatever happened above it.
 */
async function start(): Promise<void> {
  let boot
  try {
    boot = await runBoot()
  } catch (e) {
    console.error('[boot] failed; starting on the local save', e)
    boot = null
  }

  // The dynamic import is the point: this is the first moment the store module
  // is allowed to evaluate, and localStorage is now correct.
  const { mountApp, exposeStoreForDrivers } = await import('./mountApp')
  exposeStoreForDrivers()
  // Hand the boot verdict to the app before it renders, so the first paint can
  // already show "saving is paused" rather than discovering it a second later.
  ;(window as unknown as { __prBootResult?: unknown }).__prBootResult = boot
  mountApp()
}

void start()
